import { useEffect, useState } from 'react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../design/components/Card';
import { Badge, DifficultyBadge, GradeBadge } from '../design/components/Badge';
import { Button } from '../design/components/Button';
import { Select } from '../design/components/Input';
import { Book, GraduationCap, FileText, Play } from 'lucide-react';

interface VCEUnit {
  unit: number;
  title: string;
  titleCn: string;
  areasOfStudy: Array<{
    name: string;
    nameCn: string;
    keyKnowledge: string[];
    keySkills?: string[];
  }>;
}

interface VCEStudyDesign {
  subject: string;
  studyCode: string;
  name: string;
  nameCn: string;
  accreditationPeriod: string;
}

type SubjectType = 'general' | 'methods' | 'specialist';

export function VCEMathematicsPage() {
  const [studyDesign, setStudyDesign] = useState<VCEStudyDesign | null>(null);
  const [units, setUnits] = useState<VCEUnit[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<SubjectType>('methods');
  const [selectedUnit, setSelectedUnit] = useState<number>(1);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchVCEData();
  }, [selectedSubject]);

  async function fetchVCEData() {
    setLoading(true);
    try {
      const [designRes, unitsRes] = await Promise.all([
        fetch('/api/vce/mathematics/study-design'),
        fetch(`/api/vce/mathematics/units/${selectedSubject}`),
      ]);

      if (designRes.ok) {
        const data = await designRes.json();
        setStudyDesign(data.data);
      }

      if (unitsRes.ok) {
        const data = await unitsRes.json();
        setUnits(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch VCE data:', error);
    } finally {
      setLoading(false);
    }
  }

  const currentUnit = units.find((u) => u.unit === selectedUnit);

  const subjectOptions = [
    { value: 'general', label: 'General Mathematics / 普通数学' },
    { value: 'methods', label: 'Mathematical Methods / 数学方法' },
    { value: 'specialist', label: 'Specialist Mathematics / 专业数学' },
  ];

  const unitOptions = [1, 2, 3, 4].map((n) => ({
    value: String(n),
    label: `Unit ${n}`,
  }));

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">VCE Mathematics</h1>
          <p className="text-slate-500 mt-1">
            {studyDesign?.accreditationPeriod} Victorian Certificate of Education
          </p>
        </div>
        <div className="flex items-center gap-2">
          <GradeBadge grade={selectedUnit >= 3 ? 12 : 11} />
        </div>
      </div>

      <div className="flex flex-wrap gap-4">
        <Select
          label="科目类型"
          options={subjectOptions}
          value={selectedSubject}
          onChange={(e) => setSelectedSubject(e.target.value as SubjectType)}
        />
        <Select
          label="单元"
          options={unitOptions}
          value={String(selectedUnit)}
          onChange={(e) => setSelectedUnit(parseInt(e.target.value))}
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center min-h-[300px]">
          <div className="animate-spin w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full" />
        </div>
      ) : (
        <>
          {currentUnit && (
            <Card variant="bordered">
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <GraduationCap className="w-5 h-5 text-blue-500" />
                      {currentUnit.title}
                    </CardTitle>
                    <CardDescription>{currentUnit.titleCn}</CardDescription>
                  </div>
                  <Button variant="primary" size="sm" leftIcon={<Play className="w-4 h-4" />}>
                    开始学习
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  {currentUnit.areasOfStudy.map((aos, index) => (
                    <div key={index} className="border-b border-slate-100 pb-6 last:border-0 last:pb-0">
                      <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                        <Book className="w-4 h-4 text-slate-400" />
                        {aos.name}
                        <span className="text-sm font-normal text-slate-500">/ {aos.nameCn}</span>
                      </h3>

                      <div className="mt-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                            <FileText className="w-4 h-4" />
                            Key Knowledge / 核心知识
                          </h4>
                          <ul className="space-y-1.5">
                            {aos.keyKnowledge.map((k, i) => (
                              <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                                {k}
                              </li>
                            ))}
                          </ul>
                        </div>

                        {aos.keySkills && aos.keySkills.length > 0 && (
                          <div>
                            <h4 className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Key Skills / 核心技能
                            </h4>
                            <ul className="space-y-1.5">
                              {aos.keySkills.map((s, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 flex-shrink-0" />
                                  {s}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                      </div>

                      <div className="mt-4 flex gap-2">
                        <Button variant="outline" size="sm">
                          练习题
                        </Button>
                        <Button variant="ghost" size="sm">
                          查看笔记
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          )}

          <Card variant="bordered">
            <CardHeader>
              <CardTitle>Exam Information / 考试信息</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-900">Examination 1</h4>
                  <p className="text-sm text-slate-600 mt-1">Duration: 60 minutes</p>
                  <p className="text-sm text-slate-600">Weight: 33% of study score</p>
                </div>
                <div className="p-4 bg-slate-50 rounded-lg">
                  <h4 className="font-medium text-slate-900">Examination 2</h4>
                  <p className="text-sm text-slate-600 mt-1">Duration: 120 minutes</p>
                  <p className="text-sm text-slate-600">Weight: 67% of study score</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
